import type { ReactNode } from 'react';
import { GRADE_TONE, type Grade, type Tone } from '@coachrex/shared';
import { toneClass } from '../lib/format';
import { useRepFilter } from '../lib/repFilter';
import { useMe } from '../lib/api';

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div className="page-head__titles">
        <h1 className="h1">{title}</h1>
        {subtitle && <span className="subtitle">{subtitle}</span>}
      </div>
      {actions && <div className="head-actions">{actions}</div>}
    </div>
  );
}

export function Chip({ tone, children, size }: { tone: Tone; children: ReactNode; size?: 'sm' | 'lg' }) {
  return <span className={`chip ${toneClass(tone)}${size ? ` chip--${size}` : ''}`}>{children}</span>;
}

export function GradeBadge({ grade, size }: { grade: Grade | null; size: 22 | 26 | 48 }) {
  // Grades come from the Phase 2 AI pass; until then the badge shows a neutral dash.
  if (!grade)
    return (
      <span className={`grade grade--${size} grade--none`} title="Not graded yet">
        –
      </span>
    );
  return <span className={`grade grade--${size} ${toneClass(GRADE_TONE[grade])}`}>{grade}</span>;
}

export function Pills<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string; count?: number }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="pills" role="tablist">
      {options.map((o) => (
        <button key={o.value} role="tab" aria-selected={o.value === value} className={`pill${o.value === value ? ' is-active' : ''}`} onClick={() => onChange(o.value)}>
          {o.label}
          {o.count !== undefined && <span className="pill__count"> {o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ options, value, onChange, label }: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} role="radio" aria-checked={o.value === value} className={o.value === value ? 'is-active' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function RepFilter({ reps }: { reps: { id: string; name: string }[] }) {
  const { value, set } = useRepFilter();
  const me = useMe();
  // A rep sees only their own leads; a filter by rep means nothing to them.
  if (me.data?.user.role === 'rep') return null;
  // A remembered rep that isn't in this list (inactive, or no leads this week) still shows, so the filter never lies.
  const known = value === 'all' || value === 'none' || reps.some((r) => r.id === value);
  return (
    <select aria-label="Filter by rep" className={`select-pill${value !== 'all' ? ' is-set' : ''}`} value={value} onChange={(e) => set(e.target.value)}>
      <option value="all">All reps</option>
      {reps.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
      {!known && <option value={value}>Selected rep</option>}
      <option value="none">Unassigned</option>
    </select>
  );
}

export function Skeleton({ height, width = '100%' }: { height: number; width?: number | string }) {
  return <div className="skeleton" style={{ height, width }} />;
}

export function ErrorBox({ error }: { error: unknown }) {
  return <div className="error-box">{error instanceof Error ? error.message : 'Something went wrong.'}</div>;
}
